import { mwLabel } from '../game/scoring';
import type { GameSession } from '../game/session';
import { must, replayAnimation } from './dom';
import { str } from './strings';

/** Everything the win panel shows, resolved from game state — no DOM involved. */
export interface ResultView {
  title: string;
  /** The reference's topology name, stamped under the title on a perfect. */
  topology?: string;
  /** Chips, in order. Each may contain `<b>` emphasis. */
  stats: string[];
  /** Nudge under the chips; empty when there's nothing useful to say. */
  note: string;
  /** The capstone note gets the stamped-seal treatment (STYLEGUIDE §8). */
  engineers?: boolean;
  /** One line per load-bearing number, until the player's first perfect. */
  legend?: string[];
  /**
   * How the day's field did, at any size (GDD §4.7). Absent on a grid with
   * no race, on a field with nobody in it, and for a player who sends
   * nothing.
   */
  field?: string[];
}

export interface ResultInputs {
  /** MW of the layout currently on the board, or `null` if it's gone dark. */
  nowMW: number | null;
  /**
   * A move count an older version of the game recorded for this grid, or
   * `null` where there is none. The counter is retired (GDD §9); this is a
   * reading kept, never one taken.
   */
  retiredMoves?: number | null;
  /** Name of the reference's topology, or `null` to leave it unstamped. */
  topology?: string | null;
  /**
   * Explain the numbers. On until the player's first perfect grid: cold
   * reads scatter across half the scale, and a new player has no frame for
   * why a number keeps rising (GDD §6.2).
   */
  explain?: boolean;
  /**
   * The grid on the board is today's daily, still racing. Only then may the
   * legend state the reveal bargain — practice, drills and archive days have
   * no race left, so their references are already open (GDD §4.3).
   */
  liveDaily?: boolean;
  /** The field readout's lines, already rendered. */
  field?: string[] | null;
}

/**
 * What the load-bearing numbers mean, in the operator's voice: a fact each,
 * no exclamation, no second sentence that isn't needed. Read per card, not
 * once at import, so the lines follow the language the player is reading in.
 *
 * On a live daily the last line states the reveal bargain — the win was the
 * floor, and tomorrow pays off today's curiosity (GDD §4.3, §6.2). It is
 * absent where the reveal is already open.
 */
function legend(liveDaily: boolean): string[] {
  return [str().legendMW, str().legendRank, ...(liveDaily ? [str().legendReveal] : [])];
}

/**
 * The panel is deliberately not a "you won, stop playing" screen — it keeps
 * showing the current layout against the player's best so tuning stays live.
 */
export function buildResultView(session: GameSession, inputs: ResultInputs): ResultView {
  const best = session.best;
  const tier = session.tier();
  if (!best || !tier) throw new Error('buildResultView() before the grid was ever completed');

  const { nowMW, retiredMoves, topology, explain, liveDaily, field } = inputs;
  const matched = session.matchedReference;
  // A drill's card marks a different event: the grid was energized before the
  // player touched it, and what the panel announces is the naive line beaten
  // (GDD §4.6). Its numbers compare against that line, not a cold read.
  const naiveMW = session.naiveLineMW;
  const naiveWire = session.naiveLineWire;
  const drill = session.mode === 'drill' && naiveMW != null && naiveWire != null;

  // Chips are stat columns (STYLEGUIDE §7): dim uppercase label (<i>) over a
  // mono value, the value bits grouped in a `.v` row so a unit sits beside
  // its number. The tier keeps its emoji for the share; here it is a rank in
  // plain text, in the same mono as its peers — type is the whole treatment.
  //
  // The card's readings are best, first take, rank — and only those: the
  // live output, the tower count and the streak already read in the strip
  // above the board, and a card that repeats the HUD is a longer card, not
  // a fuller one. A retired move count may follow.
  const stats: string[] = [
    `<i>${str().chipBest}</i> <span class="v"><b class="mw">${mwLabel(best.mw)}</b> <i>${str().unitMW}</i></span>`,
  ];
  if (drill) {
    // The naive line stands in for the first take: the drill's opening
    // reading is the drill's, not the player's. Every MW reading states its
    // unit — a unit on one number and not its neighbour reads as a slip.
    stats.push(
      `<i>${str().chipNaive}</i> <span class="v"><b>${mwLabel(naiveMW)}</b> <i>${str().unitMW}</i></span>`,
    );
  } else if (session.firstMW != null && mwLabel(session.firstMW) !== mwLabel(best.mw)) {
    // Only when it differs (GDD §6.2). Compared as the labels the player
    // reads, not as the raw numbers: a cold read the card would print as the
    // best beside the best is a column that says nothing.
    stats.push(
      `<i>${str().firstTake}</i> <span class="v"><b>${mwLabel(session.firstMW)}</b> <i>${str().unitMW}</i></span>`,
    );
  }
  // No target chip: the target is 100 every day (GDD §3.4), and a constant is
  // not information. The legend names the 100's owner instead.
  stats.push(`<i>${str().chipRank}</i> <span class="v"><b>${str().ranks[tier.rank]}</b></span>`);
  // Dimmed and unadorned: a fact from a version of the game that counted
  // moves, not an award this one still hands out (STYLEGUIDE §7).
  if (retiredMoves != null) {
    stats.push(
      `<span class="retired"><i>${str().chipMoves}</i> <span class="v"><b>${retiredMoves}</b></span></span>`,
    );
  }

  const engineers = matched && session.beatReference;
  // One note, and the biggest one wins: beating the engineers outranks the
  // flawless callout, which outranks having nothing left to say. On a drill
  // the note restates the lesson as wire saved against the naive line; the
  // flawless callout never applies there, because the board opens at par and
  // the first move is always a removal.
  const savedPct = drill ? Math.round((1 - best.wire / naiveWire) * 100) : null;
  const note =
    savedPct != null
      ? engineers
        ? str().noteBeatEngineers
        : matched
          ? str().noteLessWire(savedPct)
          : nowMW == null
            ? str().noteGridDark
            : str().noteLessWireTuning(savedPct)
      : matched
        ? session.beatReference
          ? str().noteBeatEngineers
          : session.flawless
            ? str().noteFlawless
            : ''
        : nowMW == null
          ? str().noteGridDark
          : str().noteKeepTuning;

  return {
    title: matched ? str().perfectGrid : drill ? str().lineBeaten : str().gridEnergized,
    ...(matched && topology ? { topology } : {}),
    stats,
    note,
    engineers,
    ...(explain ? { legend: legend(Boolean(liveDaily)) } : {}),
    ...(field?.length ? { field } : {}),
  };
}

/** Gap between chips as the card counts itself out, in ms. */
export const BEAT_GAP = 110;

export interface ShowOptions {
  /**
   * Reveal the chips one at a time instead of all at once. Reserved for
   * moments worth marking — the first solve, and the perfect grid — so that
   * routine re-tuning doesn't replay the fanfare on every tap.
   */
  stage?: boolean;
  /** Called once per chip as it lands, for the accompanying beat. */
  onBeat?: (index: number, total: number) => void;
}

/** Renders {@link ResultView}, flashing only when something actually changed. */
export class ResultPanel {
  private lastHTML = '';
  private beats: ReturnType<typeof setTimeout>[] = [];

  show(view: ResultView, opts: ShowOptions = {}): void {
    const panel = must('winPanel');
    panel.classList.add('show');
    // Publish the cadence to the stylesheet, so the CSS reveal and the JS
    // beats share one clock instead of two hardcoded 110s.
    panel.style.setProperty('--beat-gap', `${BEAT_GAP}ms`);
    must('winTitle').textContent = view.title;

    // The topology name is a stamp on the title, not a chip: it names the
    // shape the reference turned out to be, which is a fact about the day
    // rather than a reading off the player's board (GDD §3.5).
    const topo = must('winTopology');
    topo.textContent = view.topology ?? '';
    topo.hidden = !view.topology;

    const chips = view.stats
      .map((s, i) => `<span class="stat" style="--beat:${i}">${s}</span>`)
      .join('');
    // Everything below the chips keeps counting on from where they stopped, so
    // the staged reveal stays one cadence rather than restarting per block.
    let beat = view.stats.length;
    const html =
      `<span class="win-stats">${chips}</span>` +
      (view.note
        ? `<span class="win-note${view.engineers ? ' engineers' : ''}" style="--beat:${beat++}">${view.note}</span>`
        : '') +
      (view.legend ?? [])
        .map((line) => `<span class="win-legend" style="--beat:${beat++}">${line}</span>`)
        .join('') +
      // Under the player's own numbers, always: the field describes a race
      // already run and never precedes the result it places (GDD §4.7).
      (view.field ?? [])
        .map((line) => `<span class="win-field" style="--beat:${beat++}">${line}</span>`)
        .join('');

    if (html + (view.topology ?? '') === this.lastHTML && !opts.stage) return;

    const text = must('winText');
    this.clearBeats();
    text.innerHTML = html;
    text.classList.toggle('staged', Boolean(opts.stage));

    if (opts.stage) {
      replayAnimation(must('winTitle'), 'stamp');
      replayAnimation(must('winPanel'), 'celebrate');
      this.scheduleBeats(view, opts.onBeat);
    } else {
      replayAnimation(text, 'tick');
    }

    this.lastHTML = html + (view.topology ?? '');
  }

  /** Fire one beat per chip, in step with the CSS reveal. */
  private scheduleBeats(view: ResultView, onBeat?: ShowOptions['onBeat']): void {
    if (!onBeat) return;
    const total = view.stats.length;
    for (let i = 0; i < total; i++) {
      this.beats.push(setTimeout(() => onBeat(i, total), i * BEAT_GAP));
    }
  }

  private clearBeats(): void {
    for (const t of this.beats) clearTimeout(t);
    this.beats = [];
  }

  hide(): void {
    this.clearBeats();
    must('winPanel').classList.remove('show');
    this.lastHTML = '';
  }

  /** Bring the panel into view after the surge lands. */
  scrollIntoView(smooth: boolean): void {
    must('winPanel').scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
  }
}
